// EL SELECTOR DE SEGUNDOS de la preferencia de duración —
// ADR-2026-09-21-la-duracion-se-elige-al-segundo.
//
// "Automático" (sin preferencia: el agente usa 5 s) y un slider de 1 s al tope de la
// calidad elegida. Es el mismo control que la card de video tiene adentro del sheet, con la
// misma forma y el mismo paso, porque es la MISMA duración vista de cerca: si acá se
// eligiera en saltos y allá al segundo, un 8 puesto en la card no tendría dónde volver.
//
// Vive aparte porque lo usan los DOS lugares donde se toca la preferencia: la pastilla
// "Duración" de la caja y la pantalla "Preferencias de contenido" del menú "+". Duplicarlo
// sería tener dos ideas distintas de qué duraciones se pueden elegir.
//
// No es un `radiogroup`: "Automático" es un interruptor (aria-pressed) y el slider se opera
// con las flechas por su cuenta, que es lo que un `input[type=range]` ya hace bien.

import { DURACION_MINIMA, DURACION_POR_DEFECTO } from '../preferenciasDeGeneracion';

export interface SelectorDeSegundosProps {
  /** Los segundos elegidos, o `null` = Automático. */
  valor: number | null;
  /** El tope de la calidad de video que esté puesta (15 s en Estándar, 30 s en Profesional). */
  tope: number;
  onCambiar: (segundos: number | null) => void;
  /** Para que el `<label>` del slider apunte al input correcto cuando hay dos en la página
   *  (la pastilla de la caja y la pantalla del menú pueden estar montadas a la vez). */
  id: string;
}

export const SelectorDeSegundos: React.FC<SelectorDeSegundosProps> = ({
  valor,
  tope,
  onCambiar,
  id,
}) => {
  const automatico = valor === null;
  // En Automático el slider muestra los 5 s que de verdad corre el motor, no un 0 ni el
  // mínimo: es lo que va a pasar si no se toca nada, y moverlo desde ahí es el gesto que
  // convierte "lo que decida el agente" en una preferencia propia.
  const segundos = Math.min(valor ?? DURACION_POR_DEFECTO, tope);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          aria-pressed={automatico}
          onClick={() => onCambiar(null)}
          className={`shrink-0 rounded-full py-1.5 px-3 text-[12.5px] font-semibold transition-colors ${
            automatico
              ? 'bg-[#E9EDFD] text-brand-primary ring-1 ring-brand-primary'
              : 'bg-neutral-100 text-[#02062BE6] hover:bg-neutral-200'
          }`}
        >
          Automático
        </button>
        <input
          id={id}
          type="range"
          min={DURACION_MINIMA}
          max={tope}
          step={1}
          value={segundos}
          aria-label="Segundos del video"
          // Mover el slider ELIGE: en Automático el valor ya se ve, así que si moverlo no
          // guardara nada el control mentiría sobre lo que está mostrando.
          onChange={(e) => onCambiar(parseInt(e.target.value, 10))}
          className="min-w-0 flex-1 accent-brand-primary"
        />
        <span
          className={`w-[42px] shrink-0 text-right text-[12.5px] font-semibold tabular-nums ${
            automatico ? 'text-[#02062B]/[0.45]' : 'text-brand-primary'
          }`}
        >
          {segundos} s
        </span>
      </div>
      <div className="flex items-center justify-between px-0.5 text-[11px] tabular-nums text-[#02062B]/[0.45]">
        <span>{DURACION_MINIMA} s</span>
        {/* El tope no es una constante: baja a 15 s con la calidad Estándar, y decirlo acá
            es lo que explica por qué el slider "se corta antes" después de cambiarla. */}
        <span>{tope} s</span>
      </div>
    </div>
  );
};

export default SelectorDeSegundos;
